// Leitura da planilha de importação de preços (ticket #25, multi-hotel).
// Puro: sem DOM — dá para testar direto.

import { parseReais } from "../../domain/money.ts";
import { hojeISO } from "../../domain/datas.ts";
import {
  precoBaseVigente,
  precoClienteVigente,
} from "../../domain/precificacao.ts";
import type { Snapshot, TipoPeca } from "../../domain/snapshot.ts";
import type { ItemPrecoComando } from "../../comandos/preco.ts";

/** "base" ou o id de um Cliente. */
export type Alvo = "base" | string;

/** Uma coluna de preço do arquivo (depois da coluna do nome da peça). */
export interface ColunaImport {
  rotulo: string;
  /** Para qual tabela vai — "base", id de Cliente, ou `null` = não reconhecida. */
  alvo: Alvo | null;
  nota: string;
}
export interface LinhaImport {
  rawNome: string;
  tipo: TipoPeca | null;
  nota: string;
  /** Um valor por coluna de `colunas` (mesma ordem). */
  celulas: (number | null)[];
}
export interface AnaliseImport {
  colunas: ColunaImport[];
  linhas: LinhaImport[];
}

function norm(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

function tentarPreco(raw: string): number | null {
  if (raw.trim() === "") return null;
  try {
    return parseReais(raw);
  } catch {
    return null;
  }
}

/**
 * CSV da matriz de preços: `tipo de peça;Preço base;<Hotel>…` + uma linha por
 * peça, com os valores **explícitos** vigentes (célula vazia = herda o base). É
 * o mesmo formato que `analisarImport` lê — exportar e reimportar não muda nada.
 */
export function matrizCsv(
  snap: Pick<Snapshot, "tiposPeca" | "clientes" | "precosBase" | "precosCliente">,
  opts: { tipoIds?: ReadonlySet<string>; hotelIds?: readonly string[] } = {},
): string {
  const hoje = hojeISO();
  const reais = (c: number): string => (c / 100).toFixed(2).replace(".", ",");
  const hoteis = [...snap.clientes]
    .filter((c) => c.ativo && (!opts.hotelIds || opts.hotelIds.includes(c.id)))
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  const tipos = [...snap.tiposPeca]
    .filter((t) => t.ativo && (!opts.tipoIds || opts.tipoIds.has(t.id)))
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

  const linhas = [
    ["tipo de peça", "Preço base", ...hoteis.map((h) => h.nome)].join(";"),
  ];
  for (const t of tipos) {
    const base = precoBaseVigente(snap, t.id, hoje);
    linhas.push(
      [
        t.nome,
        base == null ? "" : reais(base),
        ...hoteis.map((h) => {
          const p = precoClienteVigente(snap, h.id, t.id, hoje);
          return p == null ? "" : reais(p);
        }),
      ].join(";"),
    );
  }
  return linhas.join("\r\n");
}

/**
 * Traduz a análise nos itens do comando: linha com Tipo × coluna com alvo ×
 * célula preenchida.
 *
 * Estava dentro do `click` handler de `_precos-io.ts`, sem teste. É aqui que a
 * coluna vira alvo, e um erro de índice entre `colunas[j]` e `celulas[j]`
 * aplicaria o preço **no Cliente errado** — nada no repositório pegava isso.
 */
export function itensDaAnalise(a: AnaliseImport): ItemPrecoComando[] {
  const itens: ItemPrecoComando[] = [];
  for (const linha of a.linhas) {
    const tipo = linha.tipo;
    if (!tipo) continue;
    a.colunas.forEach((col, j) => {
      const v = linha.celulas[j];
      if (col.alvo == null || v == null) return;
      itens.push(
        col.alvo === "base"
          ? { escopo: "base", tipoId: tipo.id, centavos: v }
          : { escopo: "cliente", clienteId: col.alvo, tipoId: tipo.id, centavos: v },
      );
    });
  }
  return itens;
}

/**
 * Quantos preços de `a` de fato serão aplicados.
 *
 * Deriva de `itensDaAnalise` de propósito: eram duas travessias com a mesma
 * regra escrita duas vezes, e nada impedia que divergissem — a tela diria
 * "12 preços" e aplicaria 11.
 */
export function contarAplicaveis(a: AnaliseImport): number {
  return itensDaAnalise(a).length;
}

/**
 * Lê a planilha colada. A 1ª linha é o cabeçalho: 1ª célula = nome da peça, as
 * demais nomeiam as tabelas ("Preço base" ou um hotel). Sem cabeçalho
 * reconhecível, usa uma coluna só, no alvo `alvoImplicito`.
 *
 * Ignora colunas de hotéis inexistentes/inativos e linhas de peças fora do
 * catálogo — mas mantém tudo no resultado, com a `nota` do motivo.
 */
export function analisarImport(
  texto: string,
  snap: Pick<Snapshot, "tiposPeca" | "clientes">,
  alvoImplicito: Alvo,
): AnaliseImport {
  const cruas = texto
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (cruas.length === 0) return { colunas: [], linhas: [] };

  const sepDe = (l: string): string =>
    l.includes("\t") ? "\t" : l.includes(";") ? ";" : ",";

  const porNomeTipo = new Map(snap.tiposPeca.map((t) => [norm(t.nome), t]));
  const porNomeCliente = new Map(snap.clientes.map((c) => [norm(c.nome), c]));
  const ehBase = (s: string): boolean =>
    ["preco base", "base", "tabela base", "preco"].includes(norm(s));

  // --- cabeçalho ---
  const primeira = cruas[0]!.split(sepDe(cruas[0]!)).map((p) => p.trim());
  const temCabecalho =
    primeira.length >= 2 && primeira.slice(1).every((c) => tentarPreco(c) == null);

  let colunas: ColunaImport[];
  let corpo: string[];
  if (temCabecalho) {
    colunas = primeira.slice(1).map((rotulo): ColunaImport => {
      if (ehBase(rotulo)) return { rotulo, alvo: "base", nota: "" };
      const cli = porNomeCliente.get(norm(rotulo));
      if (cli && cli.ativo) return { rotulo, alvo: cli.id, nota: "" };
      if (cli) return { rotulo, alvo: null, nota: "Hotel inativo" };
      return { rotulo, alvo: null, nota: "Hotel não encontrado" };
    });
    corpo = cruas.slice(1);
  } else {
    const rotulo =
      alvoImplicito === "base"
        ? "Preço base"
        : (snap.clientes.find((c) => c.id === alvoImplicito)?.nome ?? "Hotel");
    colunas = [{ rotulo, alvo: alvoImplicito, nota: "" }];
    corpo = cruas;
  }

  // --- linhas ---
  const linhas: LinhaImport[] = corpo.map((l) => {
    const partes = l.split(sepDe(l)).map((p) => p.trim());
    const rawNome = partes[0] ?? "";
    const achado = porNomeTipo.get(norm(rawNome));
    const tipo = achado && achado.ativo ? achado : null;
    const nota = !achado
      ? "Peça não encontrada"
      : !achado.ativo
        ? "Peça inativa"
        : "";
    const celulas = colunas.map((_, j) => tentarPreco(partes[j + 1] ?? ""));
    return { rawNome, tipo, nota, celulas };
  });

  return { colunas, linhas };
}
